import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { expertDashboardApi } from '../../../lib/http';
import DateTimeSection from './DateTimeSection';
import ClientSelector from './ClientSelector';
import ServiceSelector from './ServiceSelector';
import BookingSummary from './BookingSummary';

interface CreateBookingPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prefilledDate?: string;
  prefilledTime?: string;
  prefilledClient?: {
    id: string;
    name: string;
    phone: string;
    email: string;
  } | null;
  timezone?: string;
  workingHoursEnd?: string | null;
}

interface FormData {
  date: Date | null;
  startTime: string | null;
  endTime: string | null;
  clientId: string | null;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  serviceId: string | null;
  serviceName: string;
  serviceDuration: number;
  servicePrice: number;
  serviceCurrency: string;
  notes: string;
}

interface FormErrors {
  date?: string;
  time?: string;
  client?: string;
  service?: string;
  general?: string;
}

const CreateBookingPanel: React.FC<CreateBookingPanelProps> = ({
  isOpen,
  onClose,
  onSuccess,
  prefilledDate,
  prefilledTime,
  prefilledClient,
  timezone,
  workingHoursEnd,
}) => {
  const [formData, setFormData] = useState<FormData>({
    date: null,
    startTime: null,
    endTime: null,
    clientId: null,
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    serviceId: null,
    serviceName: '',
    serviceDuration: 0,
    servicePrice: 0,
    serviceCurrency: 'USD',
    notes: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Set prefilled data when panel opens
  useEffect(() => {
    if (isOpen && (prefilledDate || prefilledTime)) {
      setFormData(prev => ({
        ...prev,
        date: prefilledDate ? new Date(prefilledDate) : null,
        startTime: prefilledTime || null,
      }));
    }
  }, [isOpen, prefilledDate, prefilledTime]);

  // Set prefilled client when panel opens
  useEffect(() => {
    if (isOpen && prefilledClient) {
      setFormData(prev => ({
        ...prev,
        clientId: prefilledClient.id,
        clientName: prefilledClient.name,
        clientPhone: prefilledClient.phone,
        clientEmail: prefilledClient.email,
      }));
    }
  }, [isOpen, prefilledClient]);

  // Track unsaved changes
  useEffect(() => {
    const hasChanges = !!(
      formData.date ||
      formData.startTime ||
      formData.clientId ||
      formData.serviceId ||
      formData.notes
    );
    setHasUnsavedChanges(hasChanges);
  }, [formData]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, hasUnsavedChanges]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.startTime) {
      newErrors.time = 'Time is required';
    }

    if (!formData.clientId) {
      newErrors.client = 'Please select or add a client';
    }

    if (!formData.serviceId) {
      newErrors.service = 'Please select a service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Format date and time as local time string (in expert's timezone)
      // We send the time as the expert sees it on the calendar, without UTC conversion
      const date = formData.date!;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const timeStr = formData.startTime!; // HH:mm

      // Send as local time string without timezone suffix
      // Backend will interpret this in the expert's timezone
      const startsAtLocal = `${dateStr}T${timeStr}:00`;

      const response = await expertDashboardApi.bookings.create({
        expert_client_id: formData.clientId!,
        service_id: formData.serviceId!,
        starts_at: startsAtLocal,
        notes: formData.notes || undefined,
      });

      toast.success('Booking created successfully');
      onSuccess();
      handleClose(true);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.errors?.join(', ') ||
        error.response?.data?.error ||
        'Failed to create booking. Please try again.';

      setErrors({ general: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (force = false) => {
    if (!force && hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }

    setFormData({
      date: null,
      startTime: null,
      endTime: null,
      clientId: null,
      clientName: '',
      clientPhone: '',
      clientEmail: '',
      serviceId: null,
      serviceName: '',
      serviceDuration: 0,
      servicePrice: 0,
      serviceCurrency: 'USD',
      notes: '',
    });
    setErrors({});
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleClientSelect = (client: {
    id: string;
    name: string;
    phone: string;
    email: string;
  }) => {
    setFormData(prev => ({
      ...prev,
      clientId: client.id,
      clientName: client.name,
      clientPhone: client.phone,
      clientEmail: client.email,
    }));
    setErrors(prev => ({ ...prev, client: undefined }));
  };

  const handleServiceSelect = (service: {
    id: string;
    name: string;
    duration_min: number;
    price_cents: number;
    currency: string;
  }) => {
    setFormData(prev => ({
      ...prev,
      serviceId: service.id,
      serviceName: service.name,
      serviceDuration: service.duration_min,
      servicePrice: service.price_cents,
      serviceCurrency: service.currency,
    }));
    setErrors(prev => ({ ...prev, service: undefined }));
  };

  const handleDateTimeChange = (date: Date | null, startTime: string | null) => {
    setFormData(prev => ({
      ...prev,
      date,
      startTime,
    }));
    setErrors(prev => ({ ...prev, date: undefined, time: undefined }));
  };

  const isFormValid = !!(
    formData.date &&
    formData.startTime &&
    formData.clientId &&
    formData.serviceId
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity"
        onClick={() => handleClose()}
        aria-hidden="true"
      />

      {/* Modal panel - centered on desktop, full-screen on mobile */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-0 md:p-4">
        <div
          ref={panelRef}
          className="w-full h-full md:w-[520px] md:h-auto md:max-h-[90vh] bg-white shadow-xl md:rounded-2xl flex flex-col transform transition-all duration-300 ease-in-out"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-booking-title"
        >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 id="create-booking-title" className="text-xl font-semibold text-gray-900">
              New Booking
            </h2>
            <button
              onClick={() => handleClose()}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close panel"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form - Scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {/* General Error */}
            {errors.general && (
              <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">{errors.general}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Date & Time Section */}
            <DateTimeSection
              date={formData.date}
              startTime={formData.startTime}
              isPrefilled={!!(prefilledDate || prefilledTime)}
              error={errors.date || errors.time}
              onChange={handleDateTimeChange}
            />

            {/* Client Section */}
            <ClientSelector
              selectedClientId={formData.clientId}
              selectedClientName={formData.clientName}
              error={errors.client}
              onClientSelect={handleClientSelect}
            />

            {/* Service Section */}
            <ServiceSelector
              selectedServiceId={formData.serviceId}
              selectedServiceName={formData.serviceName}
              error={errors.service}
              onServiceSelect={handleServiceSelect}
              startTime={formData.startTime}
              workingHoursEnd={workingHoursEnd}
            />

            {/* Notes Section */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
                maxLength={500}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-vertical"
                placeholder="Add notes about this booking..."
                style={{ minHeight: '96px', maxHeight: '240px' }}
              />
              <p className="mt-1 text-xs text-gray-500 text-right">
                {formData.notes.length}/500
              </p>
            </div>

            {/* Summary Section */}
            {isFormValid && (
              <BookingSummary
                clientName={formData.clientName}
                date={formData.date!}
                startTime={formData.startTime!}
                serviceName={formData.serviceName}
                serviceDuration={formData.serviceDuration}
                servicePrice={formData.servicePrice}
                currency={formData.serviceCurrency}
              />
            )}
          </div>
        </form>

        {/* Footer - fixed at bottom on mobile with safe area for devices with home indicator */}
        <div className="flex-shrink-0 px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-gray-200 flex gap-3 bg-white">
          <button
            type="button"
            onClick={() => handleClose()}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating...
              </span>
            ) : (
              'Create Booking'
            )}
          </button>
        </div>
        </div>
      </div>
    </>
  );
};

export default CreateBookingPanel;
